import * as React from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/core";
import classNames from "classnames";
import theme from "@/theme";

type ModalProps = {
  open: boolean;
  width?: string;
  height?: string;
  maxWidth?: string;
  maxHeight?: string;
  minHeight?: string;
  top?: string;
  left?: string;
  padding?: string;
  children:
    | [
        React.ReactElement<Parameters<typeof Body>>,
        React.ReactElement<Parameters<typeof Header>>?,
        React.ReactElement<Parameters<typeof Footer>>?
      ]
    | React.ReactElement<Parameters<typeof Body>>;
  flex?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

type ModalHeaderProps = {
  fontSize?: number;
  color?: string;
  align?: "start" | "center" | "end";
} & React.HTMLAttributes<HTMLHeadElement>;

type ModalBodyProps = { padding?: string } & React.HTMLAttributes<HTMLHeadElement>;

type ModalFooterProps = Record<string, any> & React.HTMLAttributes<HTMLDivElement>;

const fadeIn = keyframes`
  0% {
    visibility: hidden;
    background: rgba(0, 0, 0, 0);
  }
  1% {
    visibility: visible;
  }
  100% {
    background: rgba(0, 0, 0, 0.5);
  }
`;

const fadeOut = keyframes`
  0% {
    visibility: visible;
    background: rgba(0, 0, 0, 0.5);
  }
  100% {
    visibility: hidden;
    background: rgba(0, 0, 0, 0);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 100;
  overflow: hidden;

  display: flex;
  justify-content: center;
  align-items: flex-start;

  &.exiting {
    visibility: hidden;
    background: rgba(0, 0, 0, 0);
    animation: ${fadeOut} 0.1s linear 0s 1 forwards;
  }

  &.entered {
    visibility: visible;
    background: rgba(0, 0, 0, 0.5);
    animation: ${fadeIn} 0.1s linear 0s 1 forwards;
  }
`;

const Container = styled.div<
  Pick<ModalProps, "width" | "height" | "top" | "left" | "padding" | "maxWidth" | "maxHeight" | "minHeight"> & {
    flex?: boolean;
  }
>`
  position: relative;
  background: #fff;
  width: ${({ width = "auto" }) => width};
  height: ${({ height = "auto" }) => height}};
  min-height: ${({ minHeight = "auto" }) => minHeight};
  max-width: ${({ maxWidth = "auto" }) => maxWidth};
  max-height: ${({ maxHeight = "auto" }) => maxHeight};
  top: ${({ top = "0px" }) => top}};
  left: ${({ left = "0px" }) => left};
  padding: ${({ padding = "5px" }) => padding};
  border-radius: 5px;
  box-shadow: 10px 10px 15px -10px;
  overflow: hidden;
  overflow-y: scroll;

  transition: all 0.1s;
  transform: translateY(-999px);
  opacity: 0;

  ${({ flex }) => flex && "display: flex;"}
  ${({ flex }) => flex && "flex-direction: column;"}

  &.entered {
    transform: translateY(0);
    opacity: 1;
  }
`;

const Header = styled.header<Pick<ModalHeaderProps, "fontSize" | "color" | "align">>`
  width: 100%;
  border-bottom: 1px solid ${theme.main.lightGrey};
  min-height: 50px;
`;

const Body = styled.div<{ padding?: string }>`
  width: 100%;
  border-bottom: 1px solid ${theme.main.lightGrey};
  overflow: auto;
  flex: 1;
`;

const Footer = styled.footer`
  width: 100%;
  min-height: 50px;
`;

const Modal = ({ open = false, children, ...props }: ModalProps) => {
  return (
    <Overlay
      className={classNames({
        exiting: !open,
        entered: open,
      })}
    >
      <Container className={classNames({ entered: open })} {...props}>
        {children}
      </Container>
    </Overlay>
  );
};

Modal.Header = ({ children, ...props }: ModalHeaderProps) => <Header {...props}>{children}</Header>;

Modal.Body = ({ children, ...props }: ModalBodyProps) => <Body {...props}>{children}</Body>;

Modal.Footer = ({ children, ...props }: ModalFooterProps) => <Footer {...props}>{children}</Footer>;

export default Modal;
